/**
 * Recludo Reports Admin Page
 *
 * Internal page for super_admin + administrator of a Recludo-enabled tenant
 * (gated on companies/{c}.featureFlags.recludo_fund_flow_report === 'enabled'
 * by the backend; the page renders for any admin and lets the backend reject).
 *
 * Workflow: pick a period → Generate snapshot → preview totals → Share Link
 * (mints HMAC token + share URL) → Copy URL → optionally Revoke prior tokens.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { functions, db as firestore } from '../../lib/firebase';
import {
  Loader2,
  Share2,
  Copy,
  Check,
  ShieldCheck,
  ShieldAlert,
  RefreshCw,
  Trash2,
  FileBarChart2,
} from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';

interface OutletContextType {
  userData: any;
  companyData: any;
  userVisibleOffices: any[];
}

interface Bucket {
  recipient: string;
  aggregator: string;
  line: string;
  incCents: number;
  exCents: number;
  gstTreatment: 'inclusive' | 'free';
  rowCount: number;
}

interface RecipientTotal {
  recipient: string;
  line: string;
  incCents: number;
  exCents: number;
}

interface ReconciliationLine {
  stream: string;
  rctiCents: number;
  commCents: number;
  diffCents: number;
}

type ReconciliationStatus = 'matched' | 'mismatch' | 'no_data';

interface Snapshot {
  schemaVersion: number;
  companyId: string;
  companyName: string;
  periodMonth: string;
  generatedAt: string;
  generatedBy: string;
  ruleSetHash: string;
  reconciliationStatus: ReconciliationStatus;
  reconciliationDiffCents: number;
  buckets: Bucket[];
  totalsByRecipient: RecipientTotal[];
  reconciliation: ReconciliationLine[];
}

interface ShareTokenRow {
  id: string;
  generationId: string;
  periodMonth: string;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
  lastAccessedAt: string | null;
  accessCount: number;
}

const formatCurrency = (cents: number): string =>
  new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);

const formatPeriod = (periodMonth: string): string => {
  const [y, m] = periodMonth.split('-');
  const date = new Date(Number(y), Number(m) - 1);
  return date.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
};

function buildPeriodOptions(monthsBack = 18): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = 0; i < monthsBack; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    out.push(`${y}-${m}`);
  }
  return out;
}

const RecludoReportsAdminPage: React.FC = () => {
  const { userData, companyData } = useOutletContext<OutletContextType>();
  const companyId = userData?.companyId || '';

  const featureMode: string =
    companyData?.featureFlags?.recludo_fund_flow_report ?? 'off';
  const featureEnabled = featureMode === 'enabled' || featureMode === 'observe';

  const periodOptions = useMemo(() => buildPeriodOptions(), []);
  const [periodMonth, setPeriodMonth] = useState<string>(periodOptions[1] ?? periodOptions[0]);

  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [generationId, setGenerationId] = useState<string | null>(null);
  const [busy, setBusy] = useState<'generate' | 'share' | 'revoke' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [tokens, setTokens] = useState<ShareTokenRow[]>([]);

  const loadTokens = useCallback(async () => {
    if (!companyId) return;
    try {
      const ref = collection(firestore, `companies/${companyId}/recludo_report_share_tokens`);
      const q = query(ref, orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      const rows: ShareTokenRow[] = snap.docs.map((d) => {
        const data = d.data() as any;
        return {
          id: d.id,
          generationId: data.generationId,
          periodMonth: data.periodMonth,
          createdAt: data.createdAt,
          expiresAt: data.expiresAt,
          revokedAt: data.revokedAt ?? null,
          lastAccessedAt: data.lastAccessedAt ?? null,
          accessCount: data.accessCount ?? 0,
        };
      });
      setTokens(rows);
    } catch (err) {
      console.error('Failed to load share tokens', err);
    }
  }, [companyId]);

  useEffect(() => {
    if (featureEnabled) loadTokens();
  }, [featureEnabled, loadTokens]);

  const handleGenerate = useCallback(async () => {
    setBusy('generate');
    setError(null);
    setShareUrl('');
    try {
      const fn = httpsCallable<
        { companyId: string; periodMonth: string },
        { generationId: string; snapshot: Snapshot }
      >(functions, 'generateRecludoFundFlow');
      const result = await fn({ companyId, periodMonth });
      setSnapshot(result.data.snapshot);
      setGenerationId(result.data.generationId);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Failed to generate report');
    } finally {
      setBusy(null);
    }
  }, [companyId, periodMonth]);

  const handleShare = useCallback(async () => {
    if (!generationId) return;
    setBusy('share');
    setError(null);
    try {
      const fn = httpsCallable<
        { companyId: string; generationId: string; expiresInDays?: number },
        { shareUrl: string; expiresAt: string; tokenDocId: string }
      >(functions, 'generateRecludoFundFlowShareToken');
      const result = await fn({ companyId, generationId, expiresInDays: 7 });
      setShareUrl(result.data.shareUrl);
      await loadTokens();
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Failed to mint share link');
    } finally {
      setBusy(null);
    }
  }, [companyId, generationId, loadTokens]);

  const handleCopy = useCallback(async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed', err);
    }
  }, [shareUrl]);

  const handleRevoke = useCallback(
    async (tokenDocId: string) => {
      setBusy('revoke');
      setError(null);
      try {
        const fn = httpsCallable<
          { companyId: string; tokenDocId: string },
          { revoked: boolean }
        >(functions, 'revokeRecludoFundFlowShareToken');
        await fn({ companyId, tokenDocId });
        await loadTokens();
      } catch (err: any) {
        console.error(err);
        setError(err.message || 'Failed to revoke share link');
      } finally {
        setBusy(null);
      }
    },
    [companyId, loadTokens],
  );

  const recipientTotals = useMemo(() => {
    if (!snapshot) return {} as Record<string, { incCents: number; exCents: number }>;
    const out: Record<string, { incCents: number; exCents: number }> = {};
    for (const t of snapshot.totalsByRecipient) {
      out[t.recipient] ||= { incCents: 0, exCents: 0 };
      out[t.recipient].incCents += t.incCents;
      out[t.recipient].exCents += t.exCents;
    }
    return out;
  }, [snapshot]);

  if (!featureEnabled) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={FileBarChart2}
          title="Recludo Fund-Flow Report"
          subtitle="Monthly RCTI / commission waterfall — VFA pilot feature"
        />
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6 text-sm text-amber-200">
          <p className="font-semibold mb-1">Feature not enabled for this company.</p>
          <p className="text-amber-200/80">
            Recludo Fund-Flow is a VFA-pilot opt-in. Contact your VALUTTEN account manager to
            enable. Current feature flag mode:{' '}
            <code className="bg-white/10 rounded px-1.5 py-0.5">{featureMode}</code>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={FileBarChart2}
        title="Recludo Fund-Flow Report"
        subtitle="Monthly RCTI / commission waterfall — generate, share via expiring link"
      />

      {/* Generate controls */}
      <div className="bg-gray-800/50 border border-white/[0.06] rounded-xl p-5 space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-gray-400 mb-1.5">
              Period
            </label>
            <select
              value={periodMonth}
              onChange={(e) => setPeriodMonth(e.target.value)}
              className="bg-gray-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white min-w-[200px]"
            >
              {periodOptions.map((p) => (
                <option key={p} value={p}>
                  {formatPeriod(p)}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handleGenerate}
            disabled={busy === 'generate'}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
          >
            {busy === 'generate' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Generate
          </button>
          {snapshot && (
            <button
              onClick={handleShare}
              disabled={busy === 'share' || !generationId}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
            >
              {busy === 'share' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Share2 className="w-4 h-4" />
              )}
              Mint Share Link
            </button>
          )}
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {shareUrl && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-emerald-300 mb-2">
              Share link (expires in 7 days)
            </div>
            <div className="flex gap-2">
              <input
                readOnly
                value={shareUrl}
                className="flex-1 bg-gray-900 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-gray-200"
              />
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-white/15 text-white rounded-lg text-sm transition-colors"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Send this link to your Recludo accountant. They view the report at app.valutten.com —
              no PDF, no download. Revoke at any time from the table below.
            </p>
          </div>
        )}
      </div>

      {/* Snapshot preview */}
      {snapshot && (
        <div className="bg-gray-800/50 border border-white/[0.06] rounded-xl p-5 space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white">
                {formatPeriod(snapshot.periodMonth)} preview
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Generated {new Date(snapshot.generatedAt).toLocaleString('en-AU')} · Rule-set{' '}
                <code className="bg-white/10 rounded px-1">{snapshot.ruleSetHash.slice(0, 8)}</code>
              </p>
            </div>
            <ReconciliationBadge
              status={snapshot.reconciliationStatus}
              diffCents={snapshot.reconciliationDiffCents}
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                    Recipient
                  </th>
                  <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                    Aggregator
                  </th>
                  <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                    Line
                  </th>
                  <th className="text-right px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                    Inc-GST
                  </th>
                  <th className="text-right px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                    Ex-GST
                  </th>
                </tr>
              </thead>
              <tbody>
                {snapshot.buckets.map((b, i) => (
                  <tr
                    key={`${b.recipient}-${b.aggregator}-${b.line}-${b.gstTreatment}`}
                    className={i % 2 === 1 ? 'bg-white/[0.015]' : ''}
                  >
                    <td className="px-3 py-2 text-white font-medium">{b.recipient}</td>
                    <td className="px-3 py-2 text-gray-300">{b.aggregator}</td>
                    <td className="px-3 py-2 text-gray-400">{b.line}</td>
                    <td
                      className={`px-3 py-2 text-right font-mono ${
                        b.incCents < 0 ? 'text-red-300' : 'text-white'
                      }`}
                    >
                      {formatCurrency(b.incCents)}
                    </td>
                    <td
                      className={`px-3 py-2 text-right font-mono ${
                        b.exCents < 0 ? 'text-red-300' : 'text-gray-300'
                      }`}
                    >
                      {formatCurrency(b.exCents)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {Object.entries(recipientTotals).map(([recipient, t]) => (
                  <tr key={recipient} className="border-t border-white/[0.08]">
                    <td className="px-3 py-2 font-semibold text-white" colSpan={3}>
                      Total {recipient}
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-white">
                      {formatCurrency(t.incCents)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-white">
                      {formatCurrency(t.exCents)}
                    </td>
                  </tr>
                ))}
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Share token history */}
      <div className="bg-gray-800/50 border border-white/[0.06] rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Share links</h2>
          <button
            onClick={loadTokens}
            className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1.5"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        </div>
        {tokens.length === 0 ? (
          <p className="text-sm text-gray-500">No share links issued yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                    Period
                  </th>
                  <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                    Issued
                  </th>
                  <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                    Expires
                  </th>
                  <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                    Last access
                  </th>
                  <th className="text-right px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                    Hits
                  </th>
                  <th className="text-right px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                    Status
                  </th>
                  <th className="text-right px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((t, i) => {
                  const expired = new Date(t.expiresAt) < new Date();
                  const revoked = Boolean(t.revokedAt);
                  const live = !expired && !revoked;
                  return (
                    <tr key={t.id} className={i % 2 === 1 ? 'bg-white/[0.015]' : ''}>
                      <td className="px-3 py-2 text-white">{t.periodMonth && formatPeriod(t.periodMonth)}</td>
                      <td className="px-3 py-2 text-gray-400">
                        {new Date(t.createdAt).toLocaleString('en-AU')}
                      </td>
                      <td className="px-3 py-2 text-gray-400">
                        {new Date(t.expiresAt).toLocaleString('en-AU')}
                      </td>
                      <td className="px-3 py-2 text-gray-400">
                        {t.lastAccessedAt
                          ? new Date(t.lastAccessedAt).toLocaleString('en-AU')
                          : 'Never'}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-300 font-mono">
                        {t.accessCount}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {revoked ? (
                          <span className="text-xs font-medium px-2 py-0.5 rounded bg-red-500/10 text-red-300">
                            Revoked
                          </span>
                        ) : expired ? (
                          <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-500/10 text-gray-300">
                            Expired
                          </span>
                        ) : (
                          <span className="text-xs font-medium px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300">
                            Live
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {live && (
                          <button
                            onClick={() => handleRevoke(t.id)}
                            disabled={busy === 'revoke'}
                            className="inline-flex items-center gap-1 text-xs text-red-300 hover:text-red-200 disabled:opacity-50"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const ReconciliationBadge: React.FC<{ status: ReconciliationStatus; diffCents: number }> = ({
  status,
  diffCents,
}) => {
  if (status === 'matched') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-300">
        <ShieldCheck className="w-3.5 h-3.5" />
        Matched
      </span>
    );
  }
  if (status === 'no_data') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-300">
        <ShieldAlert className="w-3.5 h-3.5" />
        No RCTI data
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-red-500/10 text-red-300">
      <ShieldAlert className="w-3.5 h-3.5" />
      Mismatch ({formatCurrency(diffCents)})
    </span>
  );
};

export default RecludoReportsAdminPage;
